// ============================================================
// IE ENGINE — STAT GLYPHS (the house stat gems)
//
// Extracted from Shatterfield.js (Magnum Pass 2) so any IE surface
// can render the bespoke stat gems instead of OS-dependent emoji
// (🛡⚔♥✦) — Forge, Pack Shop, Codex, and eventually Pier/Casino.
//
//   import { StatGlyph, StatChip } from "./StatGlyph";
//
//   <StatChip kind="lyt" value={card.lyt} color="#e05555" />   // glyph + number
//   <StatGlyph kind="khao" size={10} color={gold} />           // glyph alone
//
// Kinds: "lyf" (heater shield) · "lyt" (blade + crossguard)
//        "anima" (faceted heart) · "khao"/default (four-point spark)
//
// `color` drives the fill, so buffed-gold / fractured-red tinting
// works everywhere. `bare` on StatChip drops the dark backing for
// tiny / mobile cards where every pixel of width matters.
//
// Pure view, zero state, zero theme imports. Crisp inline SVG that
// renders identically on every device.
//
// ⚠ CANON NOTE: Shatterfield.js still carries its own private twin
// of these two components (pre-extraction). The paths below are the
// EXACT same geometry — if a gem shape ever changes, change it HERE
// and fold Shatterfield onto this import at its solo pass
// (HoloFoil→Shatterfield migration). Until then, do not let the
// shapes drift.
// ============================================================

export function StatGlyph({ kind, size = 11, color = "#fff" }) {
  const s = { width: size, height: size, display: "block", flexShrink: 0 };
  if (kind === "lyf") return ( // heater shield, shaded right facet
    <svg style={s} viewBox="0 0 24 24">
      <path d="M12 2 L21 5.5 V11 C21 16.8 17.2 20.9 12 22.5 C6.8 20.9 3 16.8 3 11 V5.5 Z" fill={color} opacity="0.92" />
      <path d="M12 4.4 L18.8 7 V11 C18.8 15.6 15.9 18.9 12 20.2 Z" fill="#000" opacity="0.22" />
    </svg>
  );
  if (kind === "lyt") return ( // blade with crossguard
    <svg style={s} viewBox="0 0 24 24">
      <path d="M19.4 3.2 L21 4.8 L10.6 16.6 L12.4 18.4 L11 19.8 L8.6 17.4 L5.6 20.4 L3.8 18.6 L6.8 15.6 L4.4 13.2 L5.8 11.8 L7.6 13.6 Z" fill={color} opacity="0.94" />
    </svg>
  );
  if (kind === "anima") return ( // faceted heart, echoes the AnimaHeart
    <svg style={s} viewBox="0 0 24 24">
      <path d="M12 21 L4 12.5 C1.8 10 2.4 5.8 5.6 4.4 C7.8 3.4 10.4 4.2 12 6.2 C13.6 4.2 16.2 3.4 18.4 4.4 C21.6 5.8 22.2 10 20 12.5 Z" fill={color} opacity="0.94" />
      <path d="M12 6.2 L12 21 L4 12.5 C1.8 10 2.4 5.8 5.6 4.4 C7.8 3.4 10.4 4.2 12 6.2 Z" fill="#000" opacity="0.18" />
    </svg>
  );
  // khao — four-point spark with a bright core
  return (
    <svg style={s} viewBox="0 0 24 24">
      <path d="M12 1.5 L14.4 9.6 L22.5 12 L14.4 14.4 L12 22.5 L9.6 14.4 L1.5 12 L9.6 9.6 Z" fill={color} opacity="0.95" />
      <circle cx="12" cy="12" r="2" fill="#fff" opacity="0.8" />
    </svg>
  );
}

// StatChip — glyph + number, optionally on a slim dark backing so stats
// stay legible over any card art. `bare` drops the backing for tiny /
// mobile cards where every pixel of width matters.
export function StatChip({ kind, value, color, size = 11, fontSize = 12, bare = false }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: bare ? 2 : 3,
      lineHeight: 1.3,
      ...(bare ? {} : {
        background: "rgba(0,0,6,0.6)", borderRadius: 5,
        padding: "1px 5px 1px 4px", border: `1px solid ${color}33`,
      }),
    }}>
      <StatGlyph kind={kind} size={size} color={color} />
      <span style={{ color, fontWeight: 900, fontSize, textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>{value}</span>
    </span>
  );
}

export default StatGlyph;
